/**
 * Sales Dashboard Page
 * ====================
 * Main dashboard for sales reps showing statistics and recent activity
 *
 * Features:
 * - Dashboard statistics (uploads, conversions, etc.)
 * - Recent sessions table
 * - Quick action buttons
 * - Real-time data from backend API
 *
 * Security & Authorization:
 * ------------------------
 * - Frontend: Protected by ProtectedSalesRoute (authentication check)
 * - Backend: All endpoints use verify_sales_token middleware (JWT validation)
 * - Data Scoping: Backend filters ALL queries by sales_user_id from JWT token
 *   - dashboard-stats: WHERE sales_rep_user_id = $1 (line 637 in sales_routes.py)
 *   - recent-sessions: WHERE sales_rep_user_id = $1 (line 698 in sales_routes.py)
 * - Result: Each sales rep can ONLY see their own data (proper authorization)
 * - No IDOR vulnerability: sales_user_id extracted from authenticated JWT, not user input
 *
 * Design: Matches restaurant dashboard aesthetic
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload,
  FileText,
  Send,
  Eye,
  TrendingUp,
  CheckCircle,
  Clock,
  ArrowRight,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { salesApi, DashboardStats, Session } from '../services/api/salesApi';
import { useSalesAuth } from '../contexts/SalesAuthContext';

const SalesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSalesAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentSessions, setRecentSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load dashboard data
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        const [statsData, sessionsData] = await Promise.all([
          salesApi.getDashboardStats(),
          salesApi.getRecentSessions(5)
        ]);

        setStats(statsData);
        setRecentSessions(sessionsData.sessions);
      } catch (err: any) {
        console.error('Failed to load dashboard:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  // Format status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-400/20 text-yellow-300 border-yellow-400/30', label: 'Pending' },
      activated: { color: 'bg-green-400/20 text-green-300 border-green-400/30', label: 'Activated' },
      expired: { color: 'bg-red-400/20 text-red-300 border-red-400/30', label: 'Expired' },
      ready: { color: 'bg-blue-400/20 text-blue-300 border-blue-400/30', label: 'Ready' },
      processing: { color: 'bg-purple-400/20 text-purple-300 border-purple-400/30', label: 'Processing' },
      failed: { color: 'bg-red-400/20 text-red-300 border-red-400/30', label: 'Failed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span className={`px-2 py-1 rounded border text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white/60 animate-spin mx-auto" />
          <p className="mt-4 text-white/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-300 mx-auto" />
          <p className="mt-4 text-red-300 text-lg font-medium">Error Loading Dashboard</p>
          <p className="mt-2 text-white/60">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-4 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-3xl font-bold text-white">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="mt-2 text-white/60">
          Here's your sales activity overview
        </p>
      </motion.div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Uploads</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_uploads || 0}</p>
            </div>
            <div className="p-3 bg-blue-400/20 rounded-lg">
              <Upload className="w-6 h-6 text-blue-300" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Menus Extracted</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.menus_extracted || 0}</p>
            </div>
            <div className="p-3 bg-purple-400/20 rounded-lg">
              <FileText className="w-6 h-6 text-purple-300" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Invites Sent</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.invites_sent || 0}</p>
            </div>
            <div className="p-3 bg-yellow-400/20 rounded-lg">
              <Send className="w-6 h-6 text-yellow-300" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Conversions</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.conversions || 0}</p>
              {stats && stats.total_uploads > 0 && (
                <p className="text-green-300 text-xs mt-1">
                  {stats.conversion_rate.toFixed(1)}% rate
                </p>
              )}
            </div>
            <div className="p-3 bg-green-400/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-300" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-400/20 rounded-lg">
              <Eye className="w-6 h-6 text-orange-300" />
            </div>
            <div>
              <p className="text-white/60 text-sm">Activation Links Opened</p>
              <p className="text-2xl font-bold text-white mt-1">
                {stats?.activation_links_opened || 0}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
          className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-400/20 rounded-lg">
              <Clock className="w-6 h-6 text-indigo-300" />
            </div>
            <div>
              <p className="text-white/60 text-sm">Avg Processing Time</p>
              <p className="text-2xl font-bold text-white mt-1">
                {stats?.avg_processing_time
                  ? `${stats.avg_processing_time.toFixed(1)} min`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.7 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <button
          onClick={() => navigate('/sales-horse/upload')}
          className="flex items-center justify-between p-6 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 transition-all hover:scale-105"
        >
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6" />
            <div className="text-left">
              <p className="font-semibold">New Upload</p>
              <p className="text-sm opacity-80">Start pre-onboarding flow</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5" />
        </button>

        <button
          onClick={() => navigate('/sales-horse/sessions')}
          className="flex items-center justify-between p-6 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all hover:scale-105"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6" />
            <div className="text-left">
              <p className="font-semibold">View All Sessions</p>
              <p className="text-sm text-white/60">Browse and manage</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5" />
        </button>
      </motion.div>

      {/* Recent Sessions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.8 }}
        className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden"
      >
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">Recent Sessions</h2>
          <p className="text-white/60 text-sm mt-1">Your latest pre-onboarding activity</p>
        </div>

        {recentSessions.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-white/30 mx-auto" />
            <p className="mt-4 text-white/60">No sessions yet</p>
            <p className="text-white/40 text-sm mt-1">Upload your first menu to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Restaurant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {recentSessions.map((session) => (
                  <tr key={session.session_id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">
                        {session.restaurant_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(session.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white/80">
                        {session.menu_item_count || 0} items
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white/80">
                        {formatDate(session.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/sales-horse/sessions?session=${session.session_id}`)}
                        className="text-[#FDB913] hover:text-[#FDB913]/80 text-sm font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {recentSessions.length > 0 && (
          <div className="p-4 border-t border-white/10 text-center">
            <button
              onClick={() => navigate('/sales-horse/sessions')}
              className="text-[#FDB913] hover:text-[#FDB913]/80 text-sm font-medium inline-flex items-center gap-2"
            >
              View All Sessions
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SalesDashboard;
